import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { KeenIcon } from "@/components";
import { useLayout } from "@/providers";
import FormInput from "@/components/ui/input/FormInput";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useAuth } from "../../../hooks/useAuth";
import BtnSubmitAuth from "../../../components/ui/btnSubmitAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

const initialValues = {
  email: localStorage.getItem("email") || "",
  password: "",
  remember: !!localStorage.getItem("email"),
};

const Login = () => {
  const intl = useIntl();
  const loginSchema = Yup.object().shape({
    email: Yup.string()
      .matches(
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
        intl.formatMessage({ id: "AUTH.ERROR.INVALID_EMAIL" })
      )
      .min(6, intl.formatMessage({ id: "AUTH.ERROR.MIN_LENGTH_EMAIL" }))
      .max(50, intl.formatMessage({ id: "AUTH.ERROR.MAX_LENGTH_EMAIL" }))
      .required(intl.formatMessage({ id: "AUTH.ERROR.REQUIRED_EMAIL" })),
    password: Yup.string()
      .min(6, intl.formatMessage({ id: "AUTH.ERROR.MIN_LENGTH_PASSWORD" }))
      .max(50, intl.formatMessage({ id: "AUTH.ERROR.MAX_LENGTH_PASSWORD" }))
      .required(intl.formatMessage({ id: "AUTH.ERROR.REQUIRED_PASSWORD" })),
    remember: Yup.boolean(),
  });

  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setStatus(undefined); // Clear previous errors
      try {
        if (!login) {
          throw new Error("JWTProvider is required for this form.");
        }

        const loginData = {
          email: values.email,
          password: values.password,
        };

        await login(loginData);

        // Show success toast
        toast.success(intl.formatMessage({ id: "AUTH.LOGIN.SUCCESS" }));

        if (values.remember) {
          localStorage.setItem("email", values.email);
        } else {
          localStorage.removeItem("email");
        }

        // Redirect to intended path after successful login if present, else fallback
        const intendedPath = location.state?.from || "/agent";
        navigate(intendedPath, { replace: true });
      } catch (error) {
        let errorMessage = intl.formatMessage({
          id: "AUTH.ERROR.LOGIN_FAILED",
        });

        // Handle Axios network/timeout errors without response
        if (
          (!error?.status &&
            !error?.response &&
            (error?.code === "ERR_NETWORK" ||
              error?.message === "Network Error")) ||
          error?.code === "ECONNABORTED"
        ) {
          errorMessage = intl.formatMessage({ id: "AUTH.ERROR.NETWORK" });
        } else if (error.status) {
          // Handle different error status codes
          switch (error.status) {
            case 401:
              errorMessage = intl.formatMessage({
                id: "AUTH.ERROR.INVALID_CREDENTIALS",
              });
              break;
            case 403:
              errorMessage = intl.formatMessage({
                id: "AUTH.ERROR.ACCOUNT_NOT_ACTIVATED",
              });
              break;
            case 422:
              // Validation errors
              if (error.message) {
                errorMessage = error.message;
              } else {
                errorMessage = intl.formatMessage({
                  id: "AUTH.ERROR.VALIDATION_FAILED",
                });
              }
              break;
            case 429:
              errorMessage = intl.formatMessage({
                id: "AUTH.ERROR.TOO_MANY_REQUESTS",
              });
              break;
            case 500:
              errorMessage = intl.formatMessage({
                id: "AUTH.ERROR.SERVER_ERROR",
              });
              break;
            default:
              if (error.message) {
                errorMessage = error.message;
              }
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        setStatus(errorMessage);
        setSubmitting(false);
      }
      setLoading(false);
    },
  });

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      formik.handleSubmit();
    }
  };

  return (
    <div className="card max-w-[390px] w-full">
      <form
        className="card-body flex flex-col gap-5 p-10"
        onSubmit={formik.handleSubmit}
        onKeyDown={handleKeyDown}
        noValidate
      >
        <div className="text-center mb-2.5">
          <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
            <FormattedMessage id="AUTH.LOGIN.TITLE" />
          </h3>
          <div className="flex items-center justify-center font-medium">
            <span className="text-2sm text-gray-600 me-1.5">
              <FormattedMessage id="AUTH.LOGIN.NEED_ACCOUNT" />
            </span>
            <Link
              to={
                currentLayout?.name === "auth-branded"
                  ? "/auth/signup"
                  : "/auth/classic/signup"
              }
              className="text-2sm link text-[#50B553]"
            >
              <FormattedMessage id="AUTH.LOGIN.SIGN_UP" />
            </Link>
          </div>
        </div>

        {/* <div className="grid grid-cols-2 gap-2.5">
        <a href="#" className="btn btn-light btn-sm justify-center">
          <img src={toAbsoluteUrl('/media/brand-logos/google.svg')} className="size-3.5 shrink-0" />
          <FormattedMessage id="AUTH.LOGIN.USE_GOOGLE" />
        </a>

        <a href="#" className="btn btn-light btn-sm justify-center">
          <img src={toAbsoluteUrl('/media/brand-logos/apple-black.svg')} className="size-3.5 shrink-0 dark:hidden" />
          <img src={toAbsoluteUrl('/media/brand-logos/apple-white.svg')} className="size-3.5 shrink-0 light:hidden" />
          <FormattedMessage id="AUTH.LOGIN.USE_APPLE" />
        </a>
      </div> */}

        <div className="flex items-center gap-2">
          <span className="border-t border-gray-200 w-full"></span>
          <span className="text-2xs text-gray-500 font-medium uppercase">
            <FormattedMessage id="AUTH.LOGIN.OR" />
          </span>
          <span className="border-t border-gray-200 w-full"></span>
        </div>

        <FormInput
          label={intl.formatMessage({ id: "AUTH.EMAIL" })}
          name="email"
          type="email"
          icon={<KeenIcon icon="sms" className="text-lg" />}
          placeholder="example@email.com"
          autoComplete="off"
          error={formik.errors.email}
          touched={formik.touched.email}
          required
          {...formik.getFieldProps("email")}
        />

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <FormInput
              label={intl.formatMessage({ id: "AUTH.PASSWORD" })}
              name="password"
              type={showPassword ? "text" : "password"}
              icon={<KeenIcon icon="lock-2" className="text-lg" />}
              placeholder={intl.formatMessage({
                id: "AUTH.SIGNUP.PASSWORD_PLACEHOLDER",
              })}
              autoComplete="off"
              error={formik.errors.password}
              touched={formik.touched.password}
              required
              {...formik.getFieldProps("password")}
              labelRight={
                <Link
                  to={
                    currentLayout?.name === "auth-branded"
                      ? "/auth/reset-password"
                      : "/auth/classic/reset-password"
                  }
                  className="text-2sm link shrink-0 text-[#50B553]"
                  tabIndex={-1}
                >
                  <FormattedMessage id="AUTH.LOGIN.FORGOT_PASS" />
                </Link>
              }
            />
          </div>
        </div>

        {/* Error message display */}
        {formik.status && (
          <div className="text-red-500 text-sm text-center bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {formik.status}
          </div>
        )}

        <label className="checkbox-group">
          <input
            className="checkbox checkbox-sm"
            type="checkbox"
            {...formik.getFieldProps("remember")}
          />
          <span className="checkbox-label">
            <FormattedMessage id="AUTH.LOGIN.REMEMBER" />
          </span>
        </label>

        <BtnSubmitAuth
          loading={loading || formik.isSubmitting}
          disabled={loading || formik.isSubmitting}
        >
          {loading || formik.isSubmitting ? (
            <FormattedMessage id="AUTH.PLEASE_WAIT" />
          ) : (
            <FormattedMessage id="AUTH.LOGIN.SUBMIT" />
          )}
        </BtnSubmitAuth>
        <Link
          to={"/"}
          className="flex items-center justify-center text-sm gap-2 text-gray-700 hover:text-[#4AA642]"
          tabIndex={-1}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <FormattedMessage id="AUTH.LOGIN.BACK_TO_HOME" />
        </Link>
      </form>
    </div>
  );
};

export { Login };
